<!-- eslint-disable -->
<template>
  <v-container class="groups-container">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-6">
          <div>
            <h1 class="text-h4">{{ $t('groups.title') }}</h1>
            <p class="text-subtitle-1 text-grey">{{ $t('groups.subtitle') }}</p>
          </div>
          <v-btn color="primary" @click="showCreateDialog = true">
            <v-icon start>mdi-plus</v-icon>
            {{ $t('groups.createGroup') }}
          </v-btn>
        </div>

        <!-- Tabs -->
        <v-tabs v-model="tab" bg-color="white" color="primary" class="mb-4">
          <v-tab value="all">{{ $t('groups.allGroups') }}</v-tab>
          <v-tab value="my">{{ $t('groups.myGroups') }}</v-tab>
          <v-tab value="invites">
            {{ $t('groups.invitesTab') }}
            <v-badge v-if="pendingInvites.length > 0" :content="pendingInvites.length" color="error" inline class="ml-2"></v-badge>
          </v-tab>
        </v-tabs>

        <!-- Pending Invites Section (shown above groups when there are invites) -->
        <v-card v-if="tab !== 'invites' && pendingInvites.length > 0" elevation="2" class="mb-4 invite-highlight">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-email-outline</v-icon>
            {{ $t('groups.pendingInvites') }}
            <v-badge :content="pendingInvites.length" color="error" inline class="ml-2"></v-badge>
          </v-card-title>
          <v-card-text>
            <v-chip 
              v-for="invite in pendingInvites.slice(0, 3)" 
              :key="invite.inviteId"
              class="mr-2 mb-2"
              @click="tab = 'invites'"
            >
              {{ invite.groupName }}
            </v-chip>
            <v-btn variant="text" color="primary" size="small" @click="tab = 'invites'">
              {{ $t('common.viewAll') }}
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Feed filter info (shown on My Groups tab when any group is hidden) -->
        <v-alert 
          v-if="tab === 'my' && hiddenFeedCount > 0" 
          type="info" 
          variant="tonal" 
          class="mb-4"
          density="compact"
        >
          <v-icon start size="small">mdi-eye-off</v-icon>
          {{ $t('groups.hiddenFromFeed', { count: hiddenFeedCount }) || `${hiddenFeedCount} grupper skjult fra dit feed` }}
        </v-alert>

        <!-- Show/Hide all buttons (My Groups tab only) -->
        <div v-if="tab === 'my' && groups.some(g => g.isMember)" class="d-flex align-center ga-2 mb-4">
          <v-btn
            variant="outlined"
            size="small"
            color="success"
            @click="setAllFeedVisibility(true)"
            :loading="bulkToggling"
            :disabled="groups.filter(g => g.isMember).every(g => g.showInFeed !== false)"
          >
            <v-icon start size="16">mdi-eye</v-icon>
            {{ $t('groups.showAll') || 'Vis alle i feed' }}
          </v-btn>
          <v-btn
            variant="outlined"
            size="small"
            color="grey"
            @click="setAllFeedVisibility(false)"
            :loading="bulkToggling"
            :disabled="groups.filter(g => g.isMember).every(g => g.showInFeed === false)"
          >
            <v-icon start size="16">mdi-eye-off</v-icon>
            {{ $t('groups.hideAll') || 'Skjul alle fra feed' }}
          </v-btn>
        </div>

        <!-- Search and View Toggle (hide on invites tab) -->
        <div v-if="tab !== 'invites'" class="d-flex align-center mb-4" style="gap: 1rem;">
          <v-text-field
            v-model="searchQuery"
            :placeholder="$t('groups.searchPlaceholder')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            clearable
            style="max-width: 400px;"
            hide-details
            @update:model-value="debouncedSearch"
          ></v-text-field>
          
          <v-btn-toggle v-model="viewMode" mandatory density="compact" color="primary">
            <v-btn value="gallery" icon size="small">
              <v-icon>mdi-view-grid</v-icon>
            </v-btn>
            <v-btn value="list" icon size="small">
              <v-icon>mdi-view-list</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Loading -->
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          class="d-block mx-auto my-8"
        ></v-progress-circular>

        <!-- Invites Tab Content -->
        <template v-else-if="tab === 'invites'">
          <v-row v-if="pendingInvites.length > 0">
            <v-col
              v-for="invite in pendingInvites"
              :key="invite.inviteId"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card elevation="2">
                <v-img
                  :src="invite.coverImageUrl || `https://picsum.photos/seed/${invite.groupId}/400/200`"
                  height="120"
                  cover
                >
                  <div class="invite-overlay d-flex align-end pa-3">
                    <v-chip color="primary" size="small">
                      <v-icon start size="small">mdi-email</v-icon>
                      {{ $t('groups.invitedBy') }} {{ invite.invitedByDisplayName }}
                    </v-chip>
                  </div>
                </v-img>
                <v-card-title>{{ invite.groupName }}</v-card-title>
                <v-card-subtitle>
                  <v-icon size="small">mdi-lock</v-icon>
                  {{ $t('groups.private') }} • {{ invite.memberCount }} {{ $t('groups.members') }}
                </v-card-subtitle>
                <v-card-text v-if="invite.groupDescription">
                  {{ invite.groupDescription.substring(0, 100) }}{{ invite.groupDescription.length > 100 ? '...' : '' }}
                </v-card-text>
                <v-card-actions>
                  <v-btn 
                    color="primary" 
                    @click="respondToInvite(invite, 'accept')"
                    :loading="invite.accepting"
                  >
                    <v-icon start>mdi-check</v-icon>
                    {{ $t('groups.acceptInvite') }}
                  </v-btn>
                  <v-btn 
                    variant="outlined"
                    @click="respondToInvite(invite, 'decline')"
                    :loading="invite.declining"
                  >
                    {{ $t('groups.declineInvite') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <v-alert v-else type="info" variant="tonal">
            {{ $t('groups.noInvites') }}
          </v-alert>
        </template>

        <!-- Groups Grid View -->
        <v-row v-else-if="groups.length > 0 && viewMode === 'gallery'">
          <v-col
            v-for="group in sortedGroups"
            :key="group.groupId"
            cols="12"
            sm="6"
            md="4"
          >
            <div class="position-relative">
              <v-icon
                v-if="isFavoriteGroup(group.groupId)"
                class="favorite-star-gallery"
                color="amber-darken-1"
                size="24"
              >mdi-star</v-icon>
              
              <!-- Feed visibility toggle (only on My Groups tab for members) -->
              <v-tooltip v-if="tab === 'my' && group.isMember" location="top">
                <template #activator="{ props: tipProps }">
                  <v-btn
                    v-bind="tipProps"
                    :icon="group.showInFeed !== false ? 'mdi-eye' : 'mdi-eye-off'"
                    :color="group.showInFeed !== false ? 'success' : 'grey'"
                    size="x-small"
                    variant="flat"
                    class="feed-toggle-gallery"
                    @click.stop="toggleFeedVisibility(group)"
                    :loading="group.feedToggleLoading"
                  ></v-btn>
                </template>
                {{ group.showInFeed !== false 
                  ? ($t('groups.visibleInFeed') || 'Vises i dit feed') 
                  : ($t('groups.hiddenFromFeedSingle') || 'Skjult fra dit feed') 
                }}
              </v-tooltip>

              <group-card
                :group="group"
                @click="goToGroup(group)"
                @joined="loadGroups"
                @left="loadGroups"
              />
            </div>
          </v-col>
        </v-row>

        <!-- Groups List View -->
        <v-card v-else-if="groups.length > 0 && viewMode === 'list'" elevation="1">
          <v-list>
            <v-list-item
              v-for="group in sortedGroups"
              :key="group.groupId"
              @click="goToGroup(group)"
              class="group-list-item"
            >
              <template v-slot:prepend>
                <v-avatar size="48" rounded="lg">
                  <v-img 
                    :src="group.coverImageUrl || `https://picsum.photos/seed/${group.groupId}/100/100`"
                    cover
                  ></v-img>
                </v-avatar>
              </template>
              
              <v-list-item-title class="font-weight-medium">
                <v-icon
                  v-if="isFavoriteGroup(group.groupId)"
                  color="amber-darken-1"
                  size="16"
                  class="mr-1"
                >mdi-star</v-icon>
                {{ group.groupName }}
                <v-icon v-if="!group.isPublic" size="small" class="ml-1">mdi-lock</v-icon>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ group.memberCount }} {{ $t('groups.members') }}
                <span v-if="group.groupDescription" class="ml-2">• {{ group.groupDescription.substring(0, 60) }}{{ group.groupDescription.length > 60 ? '...' : '' }}</span>
              </v-list-item-subtitle>
              
              <template v-slot:append>
                <div class="d-flex align-center ga-2">
                  <!-- Feed toggle in list view -->
                  <v-tooltip v-if="tab === 'my' && group.isMember" location="top">
                    <template #activator="{ props: tipProps }">
                      <v-btn
                        v-bind="tipProps"
                        :icon="group.showInFeed !== false ? 'mdi-eye' : 'mdi-eye-off'"
                        :color="group.showInFeed !== false ? 'success' : 'grey'"
                        size="x-small"
                        variant="text"
                        @click.stop="toggleFeedVisibility(group)"
                        :loading="group.feedToggleLoading"
                      ></v-btn>
                    </template>
                    {{ group.showInFeed !== false 
                      ? ($t('groups.visibleInFeed') || 'Vises i dit feed') 
                      : ($t('groups.hiddenFromFeedSingle') || 'Skjult fra dit feed') 
                    }}
                  </v-tooltip>

                  <v-chip 
                    v-if="group.isMember" 
                    color="success" 
                    size="small"
                    variant="tonal"
                  >
                    {{ $t('groups.member') }}
                  </v-chip>
                  <v-chip 
                    v-else-if="group.isOwner" 
                    color="primary" 
                    size="small"
                    variant="tonal"
                  >
                    {{ $t('groups.owner') }}
                  </v-chip>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Empty State -->
        <v-alert v-else type="info" class="mt-4">
          {{ tab === 'my' ? $t('groups.noMyGroups') : $t('groups.noGroups') }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Create Group Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('groups.createGroup') }}</v-card-title>
        <v-card-text>
          <v-form ref="createFormRef" @submit.prevent="createGroup">
            <v-text-field
              v-model="newGroup.groupName"
              :label="$t('groups.groupName')"
              variant="outlined"
              :rules="[rules.required, rules.maxLength]"
              class="mb-3"
            ></v-text-field>

            <v-textarea
              v-model="newGroup.groupDescription"
              :label="$t('groups.description')"
              variant="outlined"
              rows="3"
              class="mb-3"
            ></v-textarea>

            <v-checkbox
              v-model="newGroup.isPublic"
              :label="$t('groups.publicGroup')"
              hide-details
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showCreateDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="primary" @click="createGroup" :loading="creating">
            {{ $t('groups.createGroup') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import { accessibilitySettings } from '../services/accessibility'
import GroupCard from '../components/GroupCard.vue'

const router = useRouter()
const { t } = useI18n()

const tab = ref('all')
const viewMode = ref(localStorage.getItem('groupsViewMode') || 'gallery')
const groups = ref([])
const loading = ref(false)
const searchQuery = ref('')
const showCreateDialog = ref(false)
const creating = ref(false)
const createFormRef = ref(null)
const pendingInvites = ref([])
const bulkToggling = ref(false)

const setAllFeedVisibility = async (visible) => {
  bulkToggling.value = true
  const memberGroups = groups.value.filter(g => g.isMember)
  
  try {
    await Promise.all(
      memberGroups
        .filter(g => visible ? g.showInFeed === false : g.showInFeed !== false)
        .map(g => 
          api.put(`/groups/${g.groupId}/feed-visibility`, { showInFeed: visible })
            .then(() => { g.showInFeed = visible })
            .catch(err => console.error(`Error toggling group ${g.groupId}:`, err))
        )
    )
  } finally {
    bulkToggling.value = false
  }
}

// Check if a group is the favorite
const isFavoriteGroup = (groupId) => {
  return accessibilitySettings.favoriteGroupId === groupId
}

// Count groups hidden from feed
const hiddenFeedCount = computed(() => {
  return groups.value.filter(g => g.isMember && g.showInFeed === false).length
})

// Sort groups: favorite first, then the rest
const sortedGroups = computed(() => {
  const favId = accessibilitySettings.favoriteGroupId
  if (!favId) return groups.value
  
  return [...groups.value].sort((a, b) => {
    if (a.groupId === favId) return -1
    if (b.groupId === favId) return 1
    return 0
  })
})

// Save viewMode preference when it changes
watch(viewMode, (newValue) => {
  localStorage.setItem('groupsViewMode', newValue)
})

const newGroup = reactive({
  groupName: '',
  groupDescription: '',
  isPublic: true
})

const rules = {
  required: v => !!v?.trim() || t('validation.required'),
  maxLength: v => !v || v.length <= 100 || t('validation.maxLength', { max: 100 })
}

let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    loadGroups()
  }, 300)
}

// ─── FEED VISIBILITY TOGGLE ───────────────────────────

const toggleFeedVisibility = async (group) => {
  const newValue = group.showInFeed === false ? true : false
  group.feedToggleLoading = true

  try {
    const response = await api.put(`/groups/${group.groupId}/feed-visibility`, {
      showInFeed: newValue
    })

    if (response.data.success) {
      group.showInFeed = newValue
    }
  } catch (error) {
    console.error('Error toggling feed visibility:', error)
  } finally {
    group.feedToggleLoading = false
  }
}

// ─── INVITES ───────────────────────────────────────────

const loadPendingInvites = async () => {
  try {
    const response = await api.get('/invites')
    pendingInvites.value = (response.data.invites || []).map(inv => ({
      ...inv,
      accepting: false,
      declining: false
    }))
  } catch (error) {
    console.error('Error loading invites:', error)
  }
}

const respondToInvite = async (invite, action) => {
  if (action === 'accept') {
    invite.accepting = true
  } else {
    invite.declining = true
  }
  
  try {
    const response = await api.put('/invites', {
      inviteId: invite.inviteId,
      action: action
    })
    
    if (response.data.success) {
      pendingInvites.value = pendingInvites.value.filter(i => i.inviteId !== invite.inviteId)
      
      if (action === 'accept' && response.data.groupId) {
        router.push(`/groups/${response.data.groupId}`)
      }
    }
  } catch (error) {
    console.error('Error responding to invite:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    invite.accepting = false
    invite.declining = false
  }
}

// ─── GROUPS CRUD ───────────────────────────────────────

const loadGroups = async () => {
  loading.value = true
  try {
    const params = {}
    if (tab.value === 'my') {
      params.my = 'true'
    }
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    
    const response = await api.get('/groups', { params })
    groups.value = response.data.groups || []
  } catch (error) {
    console.error('Error loading groups:', error)
  } finally {
    loading.value = false
  }
}

const createGroup = async () => {
  const { valid } = await createFormRef.value.validate()
  if (!valid) return

  creating.value = true
  try {
    const response = await api.post('/groups', newGroup)
    
    if (response.data.success) {
      showCreateDialog.value = false
      newGroup.groupName = ''
      newGroup.groupDescription = ''
      newGroup.isPublic = true
      
      router.push(`/groups/${response.data.group.groupId}`)
    }
  } catch (error) {
    console.error('Error creating group:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    creating.value = false
  }
}

const goToGroup = (group) => {
  router.push(`/groups/${group.groupId}`)
}

watch(tab, (newTab) => {
  if (newTab !== 'invites') {
    loadGroups()
  }
})

onMounted(() => {
  loadGroups()
  loadPendingInvites()
})
</script>

<style scoped>
.groups-container {
  max-width: 1400px;
  margin: 0 auto;
  padding-top: 2rem;
}

.invite-highlight {
  border-left: 4px solid rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.invite-overlay {
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  height: 100%;
}

.group-list-item {
  border-bottom: 1px solid rgba(0,0,0,0.08);
}

.group-list-item:last-child {
  border-bottom: none;
}

.group-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.position-relative {
  position: relative;
}

.favorite-star-gallery {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
}

.feed-toggle-gallery {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}
</style>